import React, { useEffect, useMemo, useState } from "react";
import {
    fetchClientDirectory,
    upsertClientProfileAdmin,
    createClientProject,
    updateClientProject,
    deleteClientProject,
    createProjectUpdate,
    deleteProjectUpdate,
    uploadProjectAsset,
    createProjectFileRecord,
    deleteProjectFile,
} from "../../services/clientAdminService";
import { isSupabaseConfigured } from "../../lib/supabaseClient";

const emptyProfileDraft = {
    id: "",
    fullName: "",
    company: "",
    phone: "",
};

const emptyProjectDraft = (clientId) => ({
    id: null,
    clientId,
    title: "",
    status: "",
    summary: "",
    kickoffDate: "",
    deliveryDate: "",
});

const emptyUpdateForm = {
    title: "",
    body: "",
    status: "bilgi",
};

const emptyFileForm = {
    file: null,
    label: "",
    note: "",
    fileType: "",
    sizeLabel: "",
};

const cloneProfile = (profile) => ({
    ...profile,
    projects: (profile?.projects ?? []).map((project) => ({
        ...project,
        files: (project.files ?? []).map((file) => ({ ...file })),
        updates: (project.updates ?? []).map((update) => ({ ...update })),
    })),
});

export default function ClientPortalManager() {
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [profileDraft, setProfileDraft] = useState(emptyProfileDraft);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [projectDraft, setProjectDraft] = useState(null);
    const [isSavingProject, setIsSavingProject] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [updateForm, setUpdateForm] = useState(emptyUpdateForm);
    const [isSavingUpdate, setIsSavingUpdate] = useState(false);
    const [fileForm, setFileForm] = useState(emptyFileForm);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isNewProfile, setIsNewProfile] = useState(false);

    const activeClient = useMemo(() => clients.find((item) => item.id === selectedClientId) ?? null, [clients, selectedClientId]);
    const activeProject = useMemo(() => activeClient?.projects.find((project) => project.id === selectedProjectId) ?? null, [activeClient, selectedProjectId]);

    useEffect(() => {
        if (!isSupabaseConfigured) {
            setIsLoading(false);
            setErrorMessage("Supabase anahtarları tanımlanmadı. Müşteri portalını yönetmek için .env içindeki Supabase URL ve anon anahtarını ekleyin.");
            return;
        }

        const bootstrap = async () => {
            try {
                await loadClients();
            } catch (loadError) {
                console.error(loadError);
                setErrorMessage("Müşteri kayıtları alınamadı. Yetki veya bağlantı ayarlarını kontrol edin.");
            } finally {
                setIsLoading(false);
            }
        };

        bootstrap();
    }, []);

    const loadClients = async ({ focusClientId, focusProjectId } = {}) => {
        setStatusMessage("");
        setErrorMessage("");
        const directory = await fetchClientDirectory();
        setClients(directory);

        const nextClientId = focusClientId || selectedClientId || directory[0]?.id || null;
        setSelectedClientId(nextClientId);

        if (nextClientId) {
            const nextClient = directory.find((item) => item.id === nextClientId);
            setProfileDraft(nextClient ? cloneProfile(nextClient) : emptyProfileDraft);
            setIsNewProfile(false);

            const fallbackProjectId = focusProjectId || selectedProjectId || nextClient?.projects[0]?.id || null;
            setSelectedProjectId(fallbackProjectId);
            if (!focusProjectId) {
                setProjectDraft(null);
            }
        } else {
            setProfileDraft(emptyProfileDraft);
            setProjectDraft(null);
            setSelectedProjectId(null);
        }
    };

    const handleSelectClient = (clientId) => {
        setSelectedClientId(clientId);
        const nextClient = clients.find((item) => item.id === clientId);
        setProfileDraft(nextClient ? cloneProfile(nextClient) : emptyProfileDraft);
        setProjectDraft(null);
        setSelectedProjectId(nextClient?.projects[0]?.id ?? null);
        setIsNewProfile(false);
        setStatusMessage("");
        setErrorMessage("");
    };

    const handleCreateProfileDraft = () => {
        setSelectedClientId(null);
        setProfileDraft({ ...emptyProfileDraft });
        setProjectDraft(null);
        setSelectedProjectId(null);
        setIsNewProfile(true);
        setStatusMessage("Yeni müşteri kaydı oluşturuyorsunuz. Supabase Users ekranından kullanıcı UUID'sini kopyalayın.");
    };

    const handleProfileChange = (field, value) => {
        setProfileDraft((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveProfile = async (event) => {
        event?.preventDefault();
        if (!profileDraft.id) {
            setErrorMessage("Supabase auth kullanıcı UUID'si olmadan profil kaydedilemez.");
            return;
        }

        try {
            setIsSavingProfile(true);
            const saved = await upsertClientProfileAdmin(profileDraft);
            await loadClients({ focusClientId: saved.id });
            setStatusMessage("Müşteri profili kaydedildi.");
        } catch (saveError) {
            console.error(saveError);
            setErrorMessage("Müşteri profili kaydedilemedi. Yetki ve admin_users tablosunu kontrol edin.");
        } finally {
            setIsSavingProfile(false);
        }
    };

    const beginProjectCreate = () => {
        if (!selectedClientId) {
            setErrorMessage("Önce bir müşteri profili seçin veya kaydedin.");
            return;
        }
        setProjectDraft(emptyProjectDraft(selectedClientId));
        setStatusMessage("Yeni proje taslağı hazır.");
    };

    const beginProjectEdit = (projectId) => {
        const target = activeClient?.projects.find((project) => project.id === projectId);
        if (!target) {
            return;
        }
        setProjectDraft({
            id: target.id,
            clientId: target.clientId,
            title: target.title,
            status: target.status,
            summary: target.summary,
            kickoffDate: target.kickoffDate || "",
            deliveryDate: target.deliveryDate || "",
        });
        setSelectedProjectId(projectId);
        setStatusMessage("Proje düzenleyebilirsiniz.");
    };

    const handleProjectDraftChange = (field, value) => {
        setProjectDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleSaveProject = async (event) => {
        event?.preventDefault();
        if (!projectDraft || !selectedClientId) {
            return;
        }
        if (!projectDraft.title?.trim()) {
            setErrorMessage("Proje başlığı zorunludur.");
            return;
        }

        try {
            setIsSavingProject(true);
            const payload = {
                clientId: selectedClientId,
                title: projectDraft.title,
                status: projectDraft.status,
                summary: projectDraft.summary,
                kickoffDate: projectDraft.kickoffDate || null,
                deliveryDate: projectDraft.deliveryDate || null,
            };

            let savedProject;
            if (projectDraft.id) {
                savedProject = await updateClientProject({ id: projectDraft.id, ...payload });
            } else {
                savedProject = await createClientProject(payload);
            }
            await loadClients({ focusClientId: selectedClientId, focusProjectId: savedProject?.id });
            setProjectDraft(null);
            setStatusMessage("Proje kaydedildi.");
        } catch (projectError) {
            console.error(projectError);
            setErrorMessage("Proje kaydedilemedi. RLS politikalarında admin hesabınızın tanımlı olduğundan emin olun.");
        } finally {
            setIsSavingProject(false);
        }
    };

    const handleDeleteProject = async (projectId) => {
        if (!window.confirm("Projeyi ve tüm içeriğini silmek istediğinize emin misiniz?")) {
            return;
        }
        try {
            await deleteClientProject(projectId);
            await loadClients({ focusClientId: selectedClientId });
            setStatusMessage("Proje silindi.");
        } catch (deleteError) {
            console.error(deleteError);
            setErrorMessage("Proje silinemedi.");
        }
    };

    const handleCreateUpdate = async (event) => {
        event?.preventDefault();
        if (!activeProject) {
            setErrorMessage("Önce bir proje seçin.");
            return;
        }
        if (!updateForm.title.trim()) {
            setErrorMessage("Güncelleme başlığı zorunludur.");
            return;
        }
        try {
            setIsSavingUpdate(true);
            await createProjectUpdate({
                projectId: activeProject.id,
                title: updateForm.title,
                body: updateForm.body,
                status: updateForm.status,
            });
            setUpdateForm(emptyUpdateForm);
            await loadClients({ focusClientId: selectedClientId, focusProjectId: activeProject.id });
            setStatusMessage("Güncelleme eklendi.");
        } catch (updateError) {
            console.error(updateError);
            setErrorMessage("Güncelleme kaydedilemedi.");
        } finally {
            setIsSavingUpdate(false);
        }
    };

    const handleDeleteUpdate = async (updateId) => {
        if (!window.confirm("Bu güncellemeyi silmek istediğinize emin misiniz?")) {
            return;
        }
        try {
            await deleteProjectUpdate(updateId);
            await loadClients({ focusClientId: selectedClientId, focusProjectId: activeProject?.id });
            setStatusMessage("Güncelleme silindi.");
        } catch (deleteError) {
            console.error(deleteError);
            setErrorMessage("Güncelleme silinemedi.");
        }
    };

    const handleFileFieldChange = (field, value) => {
        setFileForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileInputChange = (event) => {
        const nextFile = event.target.files?.[0] ?? null;
        setFileForm((prev) => ({
            ...prev,
            file: nextFile,
            label: prev.label || nextFile?.name || "",
            fileType: nextFile?.type || prev.fileType,
            sizeLabel: prev.sizeLabel || (nextFile ? `${Math.round(nextFile.size / 1024)} KB` : ""),
        }));
    };

    const handleUploadFile = async (event) => {
        event?.preventDefault();
        if (!activeProject) {
            setErrorMessage("Önce bir proje seçin.");
            return;
        }
        if (!fileForm.file) {
            setErrorMessage("Yüklenecek dosya seçilmedi.");
            return;
        }
        try {
            setIsUploadingFile(true);
            const { bucket, path } = await uploadProjectAsset({
                file: fileForm.file,
                clientId: activeClient.id,
                projectId: activeProject.id,
            });
            await createProjectFileRecord({
                projectId: activeProject.id,
                label: fileForm.label || fileForm.file.name,
                note: fileForm.note,
                fileType: fileForm.fileType || fileForm.file.type,
                sizeLabel: fileForm.sizeLabel,
                storageBucket: bucket,
                storagePath: path,
            });
            setFileForm(emptyFileForm);
            await loadClients({ focusClientId: selectedClientId, focusProjectId: activeProject.id });
            setStatusMessage("Dosya yüklendi ve projeye eklendi.");
        } catch (uploadError) {
            console.error(uploadError);
            setErrorMessage("Dosya yüklenemedi. Storage bucket ve yetkileri kontrol edin.");
        } finally {
            setIsUploadingFile(false);
        }
    };

    const handleDeleteFile = async (file) => {
        if (!window.confirm("Dosya kaydını silmek istediğinize emin misiniz?")) {
            return;
        }
        try {
            await deleteProjectFile({
                fileId: file.id,
                storageBucket: file.storageBucket,
                storagePath: file.storagePath,
            });
            await loadClients({ focusClientId: selectedClientId, focusProjectId: activeProject?.id });
            setStatusMessage("Dosya kaydı silindi.");
        } catch (deleteError) {
            console.error(deleteError);
            setErrorMessage("Dosya kaydı silinemedi.");
        }
    };

    const refreshDirectory = async () => {
        setIsRefreshing(true);
        try {
            await loadClients({ focusClientId: selectedClientId, focusProjectId: selectedProjectId });
            setStatusMessage("Müşteri listesi yenilendi.");
        } catch (refreshError) {
            console.error(refreshError);
            setErrorMessage("Müşteri listesi yenilenemedi.");
        } finally {
            setIsRefreshing(false);
        }
    };

    if (!isSupabaseConfigured) {
        return (
            <section className="client-manager__placeholder">
                <h2 className="admin-shell__title">Müşteri paneli (pasif)</h2>
                <p className="admin-shell__subtitle">
                    Supabase anahtarları tanımlanmadan müşteri projelerini yönetemezsiniz. `.env` dosyasındaki `REACT_APP_SUPABASE_URL` ve `REACT_APP_SUPABASE_ANON_KEY` değerlerini ekleyin.
                </p>
            </section>
        );
    }

    return (
        <section className="client-manager">
            <header className="client-manager__header">
                <div>
                    <h2 className="admin-shell__title" style={{ fontSize: "1.4rem" }}>Müşteri Portalı Yönetimi</h2>
                    <p className="admin-shell__subtitle" style={{ maxWidth: "780px" }}>
                        Müşteri profilleri, proje ilerlemeleri ve dosya paylaşımlarını buradan kontrol edin. Admin hesabınızı `admin_users` tablosuna eklemeyi unutmayın.
                    </p>
                </div>
                <div className="client-manager__header-actions">
                    <button type="button" className="signature-manager__button signature-manager__button--ghost" onClick={refreshDirectory} disabled={isRefreshing || isLoading}>
                        {isRefreshing ? "Yenileniyor..." : "Listeyi yenile"}
                    </button>
                    <button type="button" className="signature-manager__button" onClick={handleCreateProfileDraft}>
                        Yeni müşteri
                    </button>
                </div>
            </header>

            {(statusMessage || errorMessage) && (
                <p className={`client-manager__flash${errorMessage ? " client-manager__flash--error" : ""}`}>
                    {errorMessage || statusMessage}
                </p>
            )}

            <div className="client-manager__layout">
                <aside className="client-manager__sidebar" aria-label="Müşteri listesi">
                    {isLoading ? (
                        <p>Yükleniyor...</p>
                    ) : clients.length ? (
                        <ul className="client-manager__list">
                            {clients.map((client) => (
                                <li key={client.id}>
                                    <button
                                        type="button"
                                        className={`client-manager__list-item${client.id === selectedClientId ? " client-manager__list-item--active" : ""}`}
                                        onClick={() => handleSelectClient(client.id)}
                                    >
                                        <span className="client-manager__list-title">{client.fullName || "İsimsiz müşteri"}</span>
                                        <span className="client-manager__list-meta">{client.projects.length} proje</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>Henüz müşteri kaydı yok.</p>
                    )}
                </aside>

                <div className="client-manager__workspace">
                    <section className="client-manager__panel">
                        <header className="client-manager__panel-header">
                            <h3>Müşteri profili</h3>
                            {activeClient && !isNewProfile && <span className="client-manager__panel-meta">Supabase UID: {activeClient.id}</span>}
                        </header>
                        <form className="client-manager__form" onSubmit={handleSaveProfile}>
                            <label className="client-manager__label" htmlFor="client-profile-id">
                                Supabase Kullanıcı ID (UUID)
                            </label>
                            <input
                                id="client-profile-id"
                                className="client-manager__input"
                                value={profileDraft.id}
                                onChange={(event) => handleProfileChange("id", event.target.value.trim())}
                                placeholder="00000000-0000-0000-0000-000000000000"
                                required
                            />

                            <label className="client-manager__label" htmlFor="client-profile-name">
                                İsim / Soyisim
                            </label>
                            <input
                                id="client-profile-name"
                                className="client-manager__input"
                                value={profileDraft.fullName}
                                onChange={(event) => handleProfileChange("fullName", event.target.value)}
                                placeholder="Örn. Mehmet Petkagöz"
                            />

                            <label className="client-manager__label" htmlFor="client-profile-company">
                                Şirket
                            </label>
                            <input
                                id="client-profile-company"
                                className="client-manager__input"
                                value={profileDraft.company}
                                onChange={(event) => handleProfileChange("company", event.target.value)}
                                placeholder="Şirket adı"
                            />

                            <label className="client-manager__label" htmlFor="client-profile-phone">
                                Telefon
                            </label>
                            <input
                                id="client-profile-phone"
                                className="client-manager__input"
                                value={profileDraft.phone}
                                onChange={(event) => handleProfileChange("phone", event.target.value)}
                                placeholder="+90 ..."
                            />

                            <div className="client-manager__form-actions">
                                <button type="submit" className="signature-manager__button" disabled={isSavingProfile}>
                                    {isSavingProfile ? "Kaydediliyor..." : "Profili kaydet"}
                                </button>
                            </div>
                        </form>
                    </section>

                    <section className="client-manager__panel">
                        <header className="client-manager__panel-header">
                            <h3>Projeler</h3>
                            <button type="button" className="client-manager__panel-action" onClick={beginProjectCreate}>
                                Yeni proje
                            </button>
                        </header>

                        {activeClient ? (
                            <div className="client-manager__projects">
                                {activeClient.projects.length ? (
                                    <ul className="client-manager__project-list">
                                        {activeClient.projects.map((project) => (
                                            <li key={project.id} className={`client-manager__project-item${project.id === selectedProjectId ? " client-manager__project-item--active" : ""}`}>
                                                <div className="client-manager__project-body" role="button" tabIndex={0} onClick={() => setSelectedProjectId(project.id)} onKeyDown={(event) => event.key === "Enter" && setSelectedProjectId(project.id)}>
                                                    <div>
                                                        <h4>{project.title}</h4>
                                                        <p>{project.summary || "Özet eklenmedi."}</p>
                                                    </div>
                                                    <dl className="client-manager__project-meta">
                                                        <div>
                                                            <dt>Durum</dt>
                                                            <dd>{project.status || "-"}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Güncelleme</dt>
                                                            <dd>{project.updatedAt ? new Date(project.updatedAt).toLocaleDateString() : "-"}</dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                                <div className="client-manager__project-actions">
                                                    <button type="button" onClick={() => beginProjectEdit(project.id)}>Düzenle</button>
                                                    <button type="button" onClick={() => handleDeleteProject(project.id)}>Sil</button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>Bu müşteriye ait proje yok. "Yeni proje" ile ekleyin.</p>
                                )}

                                {projectDraft && (
                                    <form className="client-manager__form" onSubmit={handleSaveProject}>
                                        <h4>{projectDraft.id ? "Projeyi düzenle" : "Yeni proje"}</h4>
                                        <label className="client-manager__label" htmlFor="project-title">
                                            Başlık
                                        </label>
                                        <input
                                            id="project-title"
                                            className="client-manager__input"
                                            value={projectDraft.title}
                                            onChange={(event) => handleProjectDraftChange("title", event.target.value)}
                                            required
                                        />

                                        <label className="client-manager__label" htmlFor="project-status">
                                            Durum
                                        </label>
                                        <input
                                            id="project-status"
                                            className="client-manager__input"
                                            value={projectDraft.status}
                                            onChange={(event) => handleProjectDraftChange("status", event.target.value)}
                                        />

                                        <label className="client-manager__label" htmlFor="project-summary">
                                            Özet
                                        </label>
                                        <textarea
                                            id="project-summary"
                                            className="client-manager__textarea"
                                            value={projectDraft.summary}
                                            onChange={(event) => handleProjectDraftChange("summary", event.target.value)}
                                        />

                                        <div className="client-manager__form-grid">
                                            <div>
                                                <label className="client-manager__label" htmlFor="project-kickoff">
                                                    Başlangıç tarihi
                                                </label>
                                                <input
                                                    id="project-kickoff"
                                                    type="date"
                                                    className="client-manager__input"
                                                    value={projectDraft.kickoffDate || ""}
                                                    onChange={(event) => handleProjectDraftChange("kickoffDate", event.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="client-manager__label" htmlFor="project-delivery">
                                                    Teslim tarihi
                                                </label>
                                                <input
                                                    id="project-delivery"
                                                    type="date"
                                                    className="client-manager__input"
                                                    value={projectDraft.deliveryDate || ""}
                                                    onChange={(event) => handleProjectDraftChange("deliveryDate", event.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="client-manager__form-actions">
                                            <button type="submit" className="signature-manager__button" disabled={isSavingProject}>
                                                {isSavingProject ? "Kaydediliyor..." : "Projeyi kaydet"}
                                            </button>
                                            <button type="button" className="signature-manager__button signature-manager__button--ghost" onClick={() => setProjectDraft(null)}>
                                                Vazgeç
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        ) : (
                            <p>Proje yönetmek için bir müşteri seçin.</p>
                        )}
                    </section>

                    {activeProject && (
                        <section className="client-manager__panel">
                            <header className="client-manager__panel-header">
                                <h3>Güncellemeler</h3>
                            </header>

                            {activeProject.updates.length ? (
                                <ul className="client-manager__update-list">
                                    {activeProject.updates.map((update) => (
                                        <li key={update.id} className="client-manager__update-item">
                                            <div>
                                                <h4>{update.title}</h4>
                                                <p>{update.body || "-"}</p>
                                                <span className="client-manager__update-meta">Durum: {update.status || "bilgi"}</span>
                                            </div>
                                            <div className="client-manager__update-actions">
                                                <span>{update.postedAt ? new Date(update.postedAt).toLocaleString() : ""}</span>
                                                <button type="button" onClick={() => handleDeleteUpdate(update.id)}>Sil</button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>Henüz güncelleme yok.</p>
                            )}

                            <form className="client-manager__form client-manager__form--inline" onSubmit={handleCreateUpdate}>
                                <label className="client-manager__label" htmlFor="update-title">
                                    Başlık
                                </label>
                                <input
                                    id="update-title"
                                    className="client-manager__input"
                                    value={updateForm.title}
                                    onChange={(event) => setUpdateForm((prev) => ({ ...prev, title: event.target.value }))}
                                    required
                                />

                                <label className="client-manager__label" htmlFor="update-status">
                                    Durum etiketi
                                </label>
                                <input
                                    id="update-status"
                                    className="client-manager__input"
                                    value={updateForm.status}
                                    onChange={(event) => setUpdateForm((prev) => ({ ...prev, status: event.target.value }))}
                                />

                                <label className="client-manager__label" htmlFor="update-body">
                                    Açıklama
                                </label>
                                <textarea
                                    id="update-body"
                                    className="client-manager__textarea"
                                    value={updateForm.body}
                                    onChange={(event) => setUpdateForm((prev) => ({ ...prev, body: event.target.value }))}
                                />

                                <div className="client-manager__form-actions">
                                    <button type="submit" className="signature-manager__button" disabled={isSavingUpdate}>
                                        {isSavingUpdate ? "Ekleniyor..." : "Güncelleme ekle"}
                                    </button>
                                </div>
                            </form>
                        </section>
                    )}

                    {activeProject && (
                        <section className="client-manager__panel">
                            <header className="client-manager__panel-header">
                                <h3>Dosyalar</h3>
                            </header>
                            {activeProject.files.length ? (
                                <table className="client-manager__file-table">
                                    <thead>
                                        <tr>
                                            <th>Etiket</th>
                                            <th>Tip</th>
                                            <th>Boyut</th>
                                            <th>Yol</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeProject.files.map((file) => (
                                            <tr key={file.id}>
                                                <td>{file.label}</td>
                                                <td>{file.fileType || "-"}</td>
                                                <td>{file.sizeLabel || "-"}</td>
                                                <td>
                                                    <code>{`${file.storageBucket}/${file.storagePath}`}</code>
                                                </td>
                                                <td>
                                                    <button type="button" onClick={() => handleDeleteFile(file)}>Sil</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p>Bu projeye henüz dosya eklenmedi.</p>
                            )}

                            <form className="client-manager__form client-manager__form--inline" onSubmit={handleUploadFile}>
                                <label className="client-manager__label" htmlFor="file-upload-input">
                                    Dosya seç
                                </label>
                                <input id="file-upload-input" type="file" className="client-manager__input" onChange={handleFileInputChange} />

                                <label className="client-manager__label" htmlFor="file-label">
                                    Etiket
                                </label>
                                <input
                                    id="file-label"
                                    className="client-manager__input"
                                    value={fileForm.label}
                                    onChange={(event) => handleFileFieldChange("label", event.target.value)}
                                    placeholder="Örn. Final render"
                                />

                                <label className="client-manager__label" htmlFor="file-note">
                                    Not
                                </label>
                                <input
                                    id="file-note"
                                    className="client-manager__input"
                                    value={fileForm.note}
                                    onChange={(event) => handleFileFieldChange("note", event.target.value)}
                                />

                                <div className="client-manager__form-grid">
                                    <div>
                                        <label className="client-manager__label" htmlFor="file-type">
                                            Dosya tipi
                                        </label>
                                        <input
                                            id="file-type"
                                            className="client-manager__input"
                                            value={fileForm.fileType}
                                            onChange={(event) => handleFileFieldChange("fileType", event.target.value)}
                                            placeholder="Örn. application/pdf"
                                        />
                                    </div>
                                    <div>
                                        <label className="client-manager__label" htmlFor="file-size">
                                            Boyut etiketi
                                        </label>
                                        <input
                                            id="file-size"
                                            className="client-manager__input"
                                            value={fileForm.sizeLabel}
                                            onChange={(event) => handleFileFieldChange("sizeLabel", event.target.value)}
                                            placeholder="Örn. 45 MB"
                                        />
                                    </div>
                                </div>

                                <div className="client-manager__form-actions">
                                    <button type="submit" className="signature-manager__button" disabled={isUploadingFile}>
                                        {isUploadingFile ? "Yükleniyor..." : "Dosyayı ekle"}
                                    </button>
                                </div>
                            </form>
                        </section>
                    )}
                </div>
            </div>
        </section>
    );
}
